import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { withRetry, installRetryInterceptor } from '../../utils/http';

const AuthContext = createContext(null);
const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Token/user live in localStorage when "Remember me" is on, else sessionStorage
// (cleared when the browser tab closes).
function readToken() {
  return localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
}
function getCachedUser() {
  try {
    const raw = localStorage.getItem('admin_user') || sessionStorage.getItem('admin_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function clearStoredAuth() {
  [localStorage, sessionStorage].forEach(s => {
    s.removeItem('admin_token');
    s.removeItem('admin_user');
  });
}
// Which store currently holds the session (defaults to localStorage)
function activeStore() {
  return sessionStorage.getItem('admin_token') ? sessionStorage : localStorage;
}

export function AuthProvider({ children }) {
  // Initialise from cache — no loading spinner on page refresh
  const [user, setUser] = useState(() => getCachedUser());
  const [loading, setLoading] = useState(false); // Never block render
  const tokenRef = useRef(readToken());

  // Silent background re-validation: confirms token still valid after load.
  // If it fails we log the user out; if it succeeds we refresh cached data.
  // Either way the page is NOT blocked on this.
  useEffect(() => {
    const token = tokenRef.current;
    if (!token) return;

    withRetry(() => axios.get(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    }))
      .then(res => {
        setUser(res.data);
        activeStore().setItem('admin_user', JSON.stringify(res.data));
      })
      .catch(err => {
        // Only sign out on a real auth rejection (401/403). A transient/network
        // failure (e.g. a cold-start blip) must NOT log the user out — keep the
        // cached session and let the next request recover.
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          clearStoredAuth();
          setUser(null);
        }
      });
  }, []); // run once on mount only

  const login = async (username, password, remember = true, { onRetry } = {}) => {
    // Retry transient cold-start failures (502/503/504/timeout) — but never a
    // 401 (bad credentials), which surfaces immediately to the user.
    const res = await withRetry(
      () => axios.post(`${API_BASE}/api/auth/login`, { username, password }),
      { onRetry },
    );
    const { access_token } = res.data;

    // Persist to localStorage (remembered) or sessionStorage (this tab only)
    clearStoredAuth();
    const store = remember ? localStorage : sessionStorage;
    store.setItem('admin_token', access_token);
    localStorage.setItem('sb-remember', String(remember));
    tokenRef.current = access_token;

    const meRes = await withRetry(() => axios.get(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` },
    }), { onRetry });
    store.setItem('admin_user', JSON.stringify(meRes.data));
    setUser(meRes.data);
  };

  const logout = () => {
    clearStoredAuth();
    tokenRef.current = null;
    setUser(null);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const api = useMemo(() => installRetryInterceptor(axios.create({
    baseURL: API_BASE,
    headers: { Authorization: `Bearer ${tokenRef.current || ''}` },
  })), [user?.user_id]); // recreate when identity changes (login / logout), not on re-validation

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
